import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 300;
const HINT_MS = 2400;
const SWIPE_PX = 50;

interface View {
    scale: number;
    tx: number;
    ty: number;
}

/**
 * The fullscreen viewer for a programme poster: pinch, wheel and double-tap zoom, drag to pan,
 * swipe to change slide.
 *
 * **The transform is written to the element rather than held in state.** A pinch produces a
 * `pointermove` every few pixels, and routing each one through a render would make the gesture lag
 * behind the fingers doing it. React owns what is on screen; this owns how it is transformed.
 *
 * The pan is clamped so a poster can never be thrown off the stage and left unrecoverable — the
 * numbers people opened it to read are in the middle of the image.
 */
export function ProgramLightbox({
    images,
    startIndex,
    onClose
}: {
    images: readonly string[];
    startIndex: number;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const [index, setIndex] = useState(startIndex);
    const [zoomed, setZoomed] = useState(false);
    const [hintVisible, setHintVisible] = useState(true);

    const stage = useRef<HTMLDivElement>(null);
    const image = useRef<HTMLImageElement>(null);
    const view = useRef<View>({ scale: 1, tx: 0, ty: 0 });
    const pointers = useRef(new Map<number, { x: number; y: number }>());
    const gesture = useRef({
        startDistance: 0,
        startScale: 1,
        panX: 0,
        panY: 0,
        startTx: 0,
        startTy: 0
    });
    const swipe = useRef({ x: 0, y: 0, pinched: false });
    const lastTap = useRef(0);

    const apply = useCallback(() => {
        const element = image.current;

        if (element !== null) {
            const { tx, ty, scale } = view.current;

            element.style.transform = `translate(${String(tx)}px,${String(ty)}px) scale(${String(scale)})`;
        }
    }, []);

    const clamp = useCallback(() => {
        const stageElement = stage.current;
        const element = image.current;

        if (stageElement === null || element === null) {
            return;
        }

        const width = element.clientWidth * view.current.scale;
        const height = element.clientHeight * view.current.scale;
        const maxX = Math.max(0, (width - stageElement.clientWidth) / 2);
        const maxY = Math.max(0, (height - stageElement.clientHeight) / 2);

        view.current.tx = Math.max(-maxX, Math.min(maxX, view.current.tx));
        view.current.ty = Math.max(-maxY, Math.min(maxY, view.current.ty));
    }, []);

    const reset = useCallback(() => {
        view.current = { scale: 1, tx: 0, ty: 0 };
        image.current?.classList.remove('is-panning');
        setZoomed(false);
        apply();
    }, [apply]);

    /**
     * Zooms about a point on the screen, keeping whatever is under it there. The centre is read from
     * the live rectangle rather than a remembered one: a measurement taken before the image decoded
     * is wrong, and the error shows up as the poster jumping away from the fingers holding it.
     */
    const zoomTo = useCallback(
        (next: number, px: number, py: number) => {
            const element = image.current;

            if (element === null) {
                return;
            }

            const target = Math.max(MIN_SCALE, Math.min(MAX_SCALE, next));

            if (target === view.current.scale) {
                return;
            }

            const rect = element.getBoundingClientRect();
            const centreX = rect.left + rect.width / 2;
            const centreY = rect.top + rect.height / 2;
            const ratio = target / view.current.scale;

            view.current.tx += px - (px - centreX) * ratio - centreX;
            view.current.ty += py - (py - centreY) * ratio - centreY;
            view.current.scale = target;

            if (target === MIN_SCALE) {
                view.current.tx = 0;
                view.current.ty = 0;
            }

            setZoomed(target > MIN_SCALE);
            clamp();
            apply();
        },
        [apply, clamp]
    );

    const show = useCallback(
        (next: number) => {
            setIndex((next + images.length) % images.length);
            reset();
        },
        [images.length, reset]
    );

    const toggleZoom = useCallback(
        (px: number, py: number) => zoomTo(view.current.scale > 1 ? 1 : 2.5, px, py),
        [zoomTo]
    );

    // The page behind must not scroll while the viewer is over it.
    useEffect(() => {
        const previous = document.body.style.overflow;

        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previous;
        };
    }, []);

    useEffect(() => {
        const timer = window.setTimeout(() => setHintVisible(false), HINT_MS);

        return () => window.clearTimeout(timer);
    }, []);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            } else if (event.key === 'ArrowLeft') {
                show(index - 1);
            } else if (event.key === 'ArrowRight') {
                show(index + 1);
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [index, onClose, show]);

    // A rotation or a resize moves the fitted centre, so the transform has to go back to it.
    useEffect(() => {
        window.addEventListener('resize', reset);

        return () => window.removeEventListener('resize', reset);
    }, [reset]);

    // Wheel zoom must cancel the event to stop the page scrolling underneath, and React registers
    // `onWheel` passively, where cancelling is not allowed.
    useEffect(() => {
        const element = stage.current;

        if (element === null) {
            return;
        }

        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            zoomTo(view.current.scale * (event.deltaY < 0 ? 1.15 : 0.87), event.clientX, event.clientY);
        };

        element.addEventListener('wheel', onWheel, { passive: false });

        return () => element.removeEventListener('wheel', onWheel);
    }, [zoomTo]);

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.target !== image.current && event.target !== stage.current) {
            return;
        }

        stage.current?.setPointerCapture(event.pointerId);
        pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (pointers.current.size === 1) {
            gesture.current.panX = event.clientX;
            gesture.current.panY = event.clientY;
            gesture.current.startTx = view.current.tx;
            gesture.current.startTy = view.current.ty;
            swipe.current = { x: event.clientX, y: event.clientY, pinched: false };
            image.current?.classList.add('is-panning');

            // Touch has no `dblclick`, so a second tap inside the window is detected by hand.
            const now = Date.now();

            if (now - lastTap.current < DOUBLE_TAP_MS) {
                toggleZoom(event.clientX, event.clientY);
                lastTap.current = 0;
            } else {
                lastTap.current = now;
            }
        } else if (pointers.current.size === 2) {
            const [first, second] = Array.from(pointers.current.values());

            swipe.current.pinched = true;
            gesture.current.startDistance = Math.hypot(first.x - second.x, first.y - second.y);
            gesture.current.startScale = view.current.scale;
        }
    };

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (!pointers.current.has(event.pointerId)) {
            return;
        }

        pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (pointers.current.size === 2 && gesture.current.startDistance > 0) {
            const [first, second] = Array.from(pointers.current.values());
            const distance = Math.hypot(first.x - second.x, first.y - second.y);

            zoomTo(
                gesture.current.startScale * (distance / gesture.current.startDistance),
                (first.x + second.x) / 2,
                (first.y + second.y) / 2
            );
        } else if (pointers.current.size === 1 && view.current.scale > 1) {
            view.current.tx = gesture.current.startTx + (event.clientX - gesture.current.panX);
            view.current.ty = gesture.current.startTy + (event.clientY - gesture.current.panY);
            clamp();
            apply();
        }
    };

    const onPointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
        const wasLast = pointers.current.has(event.pointerId) && pointers.current.size === 1;

        pointers.current.delete(event.pointerId);

        if (pointers.current.size < 2) {
            gesture.current.startDistance = 0;
        }

        if (pointers.current.size === 0) {
            image.current?.classList.remove('is-panning');
        }

        // A horizontal drag across an un-zoomed poster changes slide; across a zoomed one it panned.
        if (wasLast && !swipe.current.pinched && view.current.scale === 1) {
            const dx = event.clientX - swipe.current.x;
            const dy = event.clientY - swipe.current.y;

            if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
                show(index + (dx < 0 ? 1 : -1));
            }
        }
    };

    return (
        <div
            className={zoomed ? 'imglb is-zoomed' : 'imglb'}
            role="dialog"
            aria-modal="true"
            aria-label={t('site.prog.image')}
            onClick={(event) => {
                if (event.target === event.currentTarget || event.target === stage.current) {
                    onClose();
                }
            }}
        >
            <button
                className="imglb-close"
                type="button"
                aria-label={t('site.prog.close')}
                onClick={onClose}
            >
                ×
            </button>
            <button
                className="imglb-nav imglb-prev"
                type="button"
                aria-label={t('site.programs.previous')}
                onClick={() => show(index - 1)}
            >
                <svg viewBox="0 0 24 24">
                    <polyline points="15 18 9 12 15 6" />
                </svg>
            </button>
            <button
                className="imglb-nav imglb-next"
                type="button"
                aria-label={t('site.programs.next')}
                onClick={() => show(index + 1)}
            >
                <svg viewBox="0 0 24 24">
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </button>
            <div
                className="imglb-stage"
                ref={stage}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
            >
                <img
                    className="imglb-img"
                    ref={image}
                    src={images[index]}
                    alt={`${t('site.prog.alt')} ${String(index + 1)}`}
                    draggable={false}
                    onLoad={reset}
                    onDoubleClick={(event) => {
                        event.preventDefault();
                        toggleZoom(event.clientX, event.clientY);
                    }}
                />
            </div>
            <div className="imglb-hint" style={{ opacity: hintVisible ? 1 : 0 }}>
                {t('site.prog.zoom_hint')}
            </div>
        </div>
    );
}
